import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, CreditCard, Settings, Users, UserCheck, Shield, CheckCircle, XCircle, Save, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card } from '../components/ui/card';
import { isFullAdmin } from '../lib/auth';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const getHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('admin_token')}` });

const PLAN_ICONS = { crianca: Users, voluntario: UserCheck, administrador: Shield };
const PLAN_COLORS = { crianca: 'from-green-500 to-emerald-600', voluntario: 'from-blue-500 to-indigo-600', administrador: 'from-purple-500 to-violet-600' };

const Mensalidades = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const status = searchParams.get('status');

  const [pricing, setPricing] = useState({});
  const [editPricing, setEditPricing] = useState({});
  const [editing, setEditing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [history, setHistory] = useState([]);
  const [tab, setTab] = useState('planos');
  const admin = isFullAdmin();

  useEffect(() => {
    fetchPricing();
    if (admin) fetchHistory();
    if (status === 'success') toast.success('Pagamento realizado com sucesso!');
    if (status === 'cancel') toast.error('Pagamento cancelado.');
  }, []);

  const fetchPricing = async () => {
    try {
      const res = await axios.get(`${API}/payments/pricing`);
      setPricing(res.data);
      const edit = {};
      for (const [k, v] of Object.entries(res.data)) {
        edit[k] = { label: v.label, amount: (v.amount / 100).toFixed(2), description: v.description };
      }
      setEditPricing(edit);
    } catch { toast.error('Erro ao carregar preços'); }
    finally { setLoading(false); }
  };

  const fetchHistory = async () => {
    try {
      const res = await axios.get(`${API}/payments/history`, { headers: getHeaders() });
      setHistory(res.data);
    } catch { /* */ }
  };

  const handleSavePricing = async () => {
    setSaving(true);
    try {
      const prices = {};
      for (const [k, v] of Object.entries(editPricing)) {
        prices[k] = { label: v.label, amount: Math.round(parseFloat(v.amount) * 100), description: v.description };
      }
      await axios.put(`${API}/payments/pricing`, { prices }, { headers: getHeaders() });
      toast.success('Precos atualizados!');
      setEditing(false);
      fetchPricing();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erro ao salvar'); }
    finally { setSaving(false); }
  };

  const handleCheckout = async (planType) => {
    const userName = localStorage.getItem('admin_name') || '';
    const userId = localStorage.getItem('admin_id') || '';
    try {
      const res = await axios.post(`${API}/payments/create-checkout`, {
        plan_type: planType,
        quantity: 1,
        payer_name: userName,
        payer_id: userId,
      });
      if (res.data.checkout_url) {
        window.location.href = res.data.checkout_url;
      } else {
        toast.error('Erro ao gerar link de pagamento');
      }
    } catch (e) { toast.error(e.response?.data?.detail || 'Erro ao processar pagamento'); }
  };

  if (loading) return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center">
      <Loader2 className="w-8 h-8 text-[#1A4D2E] animate-spin" />
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-[#1A4D2E] text-white px-4 py-4 flex items-center gap-3 sticky top-0 z-50">
        <button onClick={() => navigate(-1)} className="hover:bg-white/10 rounded-full p-1.5" data-testid="back-btn">
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div>
          <h1 className="font-nunito font-bold text-lg">Mensalidades</h1>
          <p className="text-white/60 text-xs">Planos e pagamentos</p>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-6">
        {/* Success/Cancel feedback */}
        {status === 'success' && (
          <Card className="bg-green-50 border-green-200 rounded-xl p-4 mb-6 flex items-center gap-3" data-testid="payment-success">
            <CheckCircle className="w-6 h-6 text-green-500 shrink-0" />
            <div>
              <p className="font-semibold text-green-800">Pagamento confirmado!</p>
              <p className="text-green-600 text-sm">Sua mensalidade foi processada com sucesso.</p>
            </div>
          </Card>
        )}
        {status === 'cancel' && (
          <Card className="bg-red-50 border-red-200 rounded-xl p-4 mb-6 flex items-center gap-3" data-testid="payment-cancel">
            <XCircle className="w-6 h-6 text-red-500 shrink-0" />
            <div>
              <p className="font-semibold text-red-800">Pagamento cancelado</p>
              <p className="text-red-600 text-sm">O pagamento nao foi processado.</p>
            </div>
          </Card>
        )}

        {/* Tabs */}
        {admin && (
          <div className="flex gap-2 mb-6">
            <button onClick={() => setTab('planos')} data-testid="tab-planos"
              className={`px-4 py-2 rounded-xl text-sm font-semibold transition-all ${tab === 'planos' ? 'bg-[#1A4D2E] text-white' : 'bg-white text-gray-600 border'}`}>
              Planos
            </button>
            <button onClick={() => setTab('config')} data-testid="tab-config"
              className={`px-4 py-2 rounded-xl text-sm font-semibold transition-all flex items-center gap-1 ${tab === 'config' ? 'bg-[#1A4D2E] text-white' : 'bg-white text-gray-600 border'}`}>
              <Settings className="w-3.5 h-3.5" /> Configurar Precos
            </button>
            <button onClick={() => setTab('historico')} data-testid="tab-historico"
              className={`px-4 py-2 rounded-xl text-sm font-semibold transition-all ${tab === 'historico' ? 'bg-[#1A4D2E] text-white' : 'bg-white text-gray-600 border'}`}>
              Historico
            </button>
          </div>
        )}

        {/* Plans Cards */}
        {tab === 'planos' && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {Object.entries(pricing).map(([key, plan]) => {
              const Icon = PLAN_ICONS[key] || CreditCard;
              const gradient = PLAN_COLORS[key] || 'from-gray-500 to-gray-600';
              return (
                <Card key={key} className="rounded-2xl overflow-hidden shadow-lg hover:shadow-xl transition-all" data-testid={`plan-card-${key}`}>
                  <div className={`bg-gradient-to-br ${gradient} p-6 text-white text-center`}>
                    <Icon className="w-10 h-10 mx-auto mb-3 opacity-90" />
                    <h3 className="font-nunito font-bold text-lg mb-1">{plan.label}</h3>
                    <p className="text-white/70 text-xs">{plan.description}</p>
                  </div>
                  <div className="p-6 text-center">
                    <p className="font-nunito font-bold text-3xl text-gray-800 mb-1">{plan.amount_display}</p>
                    <p className="text-gray-400 text-xs mb-4">/mes</p>
                    <Button onClick={() => handleCheckout(key)} data-testid={`pay-btn-${key}`}
                      className="w-full bg-[#1A4D2E] hover:bg-[#145A32] text-white rounded-full py-5 font-semibold">
                      <CreditCard className="w-4 h-4 mr-2" /> Assinar
                    </Button>
                  </div>
                </Card>
              );
            })}
          </div>
        )}

        {/* Admin: Configure Pricing */}
        {tab === 'config' && admin && (
          <Card className="bg-white rounded-2xl p-6 shadow-md">
            <h3 className="font-nunito font-bold text-lg text-[#1A4D2E] mb-4">Configurar Precos das Mensalidades</h3>
            <div className="space-y-6">
              {Object.entries(editPricing).map(([key, val]) => {
                const Icon = PLAN_ICONS[key] || CreditCard;
                return (
                  <div key={key} className="bg-gray-50 rounded-xl p-4">
                    <div className="flex items-center gap-2 mb-3">
                      <Icon className="w-5 h-5 text-[#1A4D2E]" />
                      <span className="font-semibold text-[#1A4D2E] capitalize">{key}</span>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                      <div>
                        <label className="text-xs font-semibold text-gray-500 block mb-1">Nome do Plano</label>
                        <Input value={val.label} data-testid={`price-label-${key}`}
                          onChange={e => setEditPricing(p => ({...p, [key]: {...p[key], label: e.target.value}}))}
                          className="rounded-lg" />
                      </div>
                      <div>
                        <label className="text-xs font-semibold text-gray-500 block mb-1">Valor (R$)</label>
                        <Input type="number" step="0.01" min="0" value={val.amount} data-testid={`price-amount-${key}`}
                          onChange={e => setEditPricing(p => ({...p, [key]: {...p[key], amount: e.target.value}}))}
                          className="rounded-lg" />
                      </div>
                      <div>
                        <label className="text-xs font-semibold text-gray-500 block mb-1">Descricao</label>
                        <Input value={val.description} data-testid={`price-desc-${key}`}
                          onChange={e => setEditPricing(p => ({...p, [key]: {...p[key], description: e.target.value}}))}
                          className="rounded-lg" />
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="flex justify-end mt-6">
              <Button onClick={handleSavePricing} disabled={saving} data-testid="save-pricing-btn"
                className="bg-[#1A4D2E] hover:bg-[#145A32] text-white rounded-full px-8">
                {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
                Salvar Precos
              </Button>
            </div>
          </Card>
        )}

        {/* Admin: Payment History */}
        {tab === 'historico' && admin && (
          <Card className="bg-white rounded-2xl p-6 shadow-md">
            <h3 className="font-nunito font-bold text-lg text-[#1A4D2E] mb-4">Histórico de Pagamentos</h3>
            {history.length === 0 ? (
              <p className="text-gray-400 text-sm text-center py-8">Nenhum pagamento registrado</p>
            ) : (
              <div className="space-y-2">
                {history.map((t, i) => (
                  <div key={i} className="flex items-center justify-between bg-gray-50 rounded-lg p-3" data-testid={`history-item-${i}`}>
                    <div>
                      <p className="font-semibold text-sm text-gray-800">{t.payer_name || 'N/A'}</p>
                      <p className="text-xs text-gray-400">{t.plan_type} - {new Date(t.created_at).toLocaleDateString('pt-BR')}</p>
                    </div>
                    <div className="text-right">
                      <p className="font-bold text-sm">R$ {(t.amount / 100).toFixed(2)}</p>
                      <span className={`text-xs px-2 py-0.5 rounded-full ${t.status === 'paid' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}`}>
                        {t.status === 'paid' ? 'Pago' : 'Pendente'}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Card>
        )}
      </div>
    </div>
  );
};

export default Mensalidades;
